import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ContactUsPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { lang } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate checkboxes
    if (!acceptTerms || !acceptPrivacy) {
      toast.error(
        language === 'ar'
          ? 'يرجى الموافقة على الشروط وسياسة الخصوصية'
          : 'Please accept the terms and privacy policy',
        {
          duration: 3000,
        }
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Get current date
      const submissionDate = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      console.log('Sending email with:', {
        service_id: process.env.REACT_APP_SERVICE_ID,
        template_id: process.env.REACT_APP_TEMPLATE_ID,
        user_id: process.env.REACT_APP_PUBLIC_KEY,
      });

      // Using EmailJS - Free email service
      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: process.env.REACT_APP_SERVICE_ID,
          template_id: process.env.REACT_APP_TEMPLATE_ID,
          user_id: process.env.REACT_APP_PUBLIC_KEY,
          template_params: {
            from_name: formData.name,
            from_email: formData.email,
            phone: formData.phone || 'Not provided',
            company: formData.company || 'Not provided',
            service: formData.service || 'General Inquiry',
            message: formData.message,
            submission_date: submissionDate,
          },
        }),
      });

      const responseText = await response.text();
      console.log('Response status:', response.status);
      console.log('Response text:', responseText);

      if (response.ok) {
        setIsSubmitted(true);
        toast.success(
          language === 'ar'
            ? 'تم إرسال الرسالة بنجاح!'
            : 'Message sent successfully!',
          {
            description: language === 'ar' ? 'سنتواصل معك قريباً!' : 'We will get back to you soon!',
            duration: 5000,
          }
        );

        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            service: '',
            message: ''
          });
          setAcceptTerms(false);
          setAcceptPrivacy(false);
        }, 5000);
      } else {
        throw new Error(responseText || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(
        language === 'ar'
          ? 'فشل إرسال الرسالة'
          : 'Failed to send message',
        {
          description: error.message || (language === 'ar' ? 'يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.' : 'Please try again or contact us directly.'),
          duration: 5000,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = {
    en: {
      title: "Get in Touch",
      subtitle: "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
      form: {
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        company: "Company Name",
        service: "Service Interested In",
        message: "Your Message",
        submit: "Send Message",
        success: "Message sent successfully! We'll get back to you soon."
      },
      services: [
        "odoo ERP",
        "Web Development",
        "Mobile Apps",
        "Cyber Security",
        "Consulting",
        "Others",
      ],
      info: {
        email: "info@adidoo.com",
        phone: "+966 55 182 6405",
        address: "Riyadh, Saudi Arabia",
        hours: "Sun - Thu: 9:00 AM - 6:00 PM"
      },
      cards: {
        email: {
          title: "Email Us",
          desc: "Our team is here to help"
        },
        call: {
          title: "Call Us",
          desc: "Mon-Fri from 9am to 6pm"
        },
        visit: {
          title: "Visit Us",
          desc: "Come say hello at our office"
        },
        hours: {
          title: "Business Hours",
          desc: "When we're available"
        }
      }
    },
    ar: {
      title: "تواصل معنا",
      subtitle: "نحب أن نسمع منك. أرسل لنا رسالة وسنرد في أقرب وقت ممكن.",
      form: {
        name: "الاسم الكامل",
        email: "البريد الإلكتروني",
        phone: "رقم الهاتف",
        company: "اسم الشركة",
        service: "الخدمة المهتم بها",
        message: "رسالتك",
        submit: "إرسال الرسالة",
        success: "تم إرسال الرسالة بنجاح! سنتواصل معك قريباً."
      },
      services: [
        "نظام أودو لإدارة الموارد المؤسسية (ERP)",
        "تطوير المواقع الإلكترونية",
        "تطبيقات الجوال",
        "الأمن السيبراني",
        "الاستشارات",
        "خدمات أخرى"
      ]
      ,
      info: {
        email: "info@adidoo.com",
        phone: "+966 55 182 6405",
        address: "الرياض، المملكة العربية السعودية",
        hours: "الأحد - الخميس: 9:00 ص - 6:00 م"
      },
      cards: {
        email: {
          title: "راسلنا",
          desc: "فريقنا هنا للمساعدة"
        },
        call: {
          title: "اتصل بنا",
          desc: "من الأحد إلى الخميس من 9 صباحاً إلى 6 مساءً"
        },
        visit: {
          title: "زرنا",
          desc: "تعال وقل مرحباً في مكتبنا"
        },
        hours: {
          title: "ساعات العمل",
          desc: "متى نكون متاحين"
        }
      }
    }
  };

  const content = contactInfo[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 to-cyan-600 py-20 px-4">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-fade-in">
            {content.title}
          </h1>
          <p className="text-xl text-teal-50 max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6 animate-bounce" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {content.form.success}
                  </h3>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {content.form.name}
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all outline-none"
                        dir={language === 'ar' ? 'rtl' : 'ltr'}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {content.form.email}
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all outline-none"
                        dir="ltr"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {content.form.phone}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all outline-none"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {content.form.company}
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all outline-none"
                        dir={language === 'ar' ? 'rtl' : 'ltr'}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      {content.form.service}
                    </label>
                    <select
                      name="service"
                      required
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all outline-none bg-white"
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                    >
                      <option value="">Select a service</option>
                      {content.services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      {content.form.message}
                    </label>
                    <textarea
                      name="message"
                      required
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all outline-none resize-none"
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                    ></textarea>
                  </div>

                  {/* Terms and Privacy Checkboxes */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="acceptTerms"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="mt-1 w-5 h-5 text-teal-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-teal-200 cursor-pointer"
                        required
                      />
                      <label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                        {language === 'ar' ? (
                          <>
                            أوافق على{' '}
                            <button
                              type="button"
                              onClick={() => navigate(`/${lang || 'ar'}/terms-of-service`)}
                              className="text-teal-600 hover:text-teal-700 underline font-semibold"
                            >
                              شروط الخدمة
                            </button>
                          </>
                        ) : (
                          <>
                            I agree to the{' '}
                            <button
                              type="button"
                              onClick={() => navigate(`/${lang || 'en'}/terms-of-service`)}
                              className="text-teal-600 hover:text-teal-700 underline font-semibold"
                            >
                              Terms of Service
                            </button>
                          </>
                        )}
                      </label>
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="acceptPrivacy"
                        checked={acceptPrivacy}
                        onChange={(e) => setAcceptPrivacy(e.target.checked)}
                        className="mt-1 w-5 h-5 text-teal-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-teal-200 cursor-pointer"
                        required
                      />
                      <label htmlFor="acceptPrivacy" className="text-sm text-gray-700 cursor-pointer" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                        {language === 'ar' ? (
                          <>
                            أوافق على{' '}
                            <button
                              type="button"
                              onClick={() => navigate(`/${lang || 'ar'}/privacy-policy`)}
                              className="text-teal-600 hover:text-teal-700 underline font-semibold"
                            >
                              سياسة الخصوصية
                            </button>
                          </>
                        ) : (
                          <>
                            I agree to the{' '}
                            <button
                              type="button"
                              onClick={() => navigate(`/${lang || 'en'}/privacy-policy`)}
                              className="text-teal-600 hover:text-teal-700 underline font-semibold"
                            >
                              Privacy Policy
                            </button>
                          </>
                        )}
                      </label>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {content.form.submit}
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* Contact Info Cards */}
          <div className="space-y-6">
            {/* Email Card */}
            <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Mail className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg mb-2">{content.cards.email.title}</h3>
              <p className="text-teal-50 text-sm mb-3">{content.cards.email.desc}</p>
              <a href={`mailto:${content.info.email}`} className="text-white font-semibold hover:underline">
                {content.info.email}
              </a>
            </div>

            {/* Phone Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-teal-100 hover:border-teal-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">{content.cards.call.title}</h3>
              <p className="text-gray-600 text-sm mb-3">{content.cards.call.desc}</p>
              <a href={`tel:${content.info.phone}`} className="text-teal-600 font-semibold hover:underline" dir="ltr">
                {content.info.phone}
              </a>
            </div>

            {/* Location Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-teal-100 hover:border-teal-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">{content.cards.visit.title}</h3>
              <p className="text-gray-600 text-sm mb-3">{content.cards.visit.desc}</p>
              <p className="text-gray-900 font-semibold">{content.info.address}</p>
            </div>

            {/* Hours Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-teal-100 hover:border-teal-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900">{content.cards.hours.title}</h3>
              <p className="text-gray-600 text-sm mb-3">{content.cards.hours.desc}</p>
              <p className="text-gray-900 font-semibold">{content.info.hours}</p>
            </div>
          </div>
        </div>

        {/* Map Section (Optional) */}
        <div className="mt-16">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="aspect-video bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center">
              <div className="text-center p-8">
                <MapPin className="w-16 h-16 text-teal-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {language === 'ar' ? 'موقعنا' : 'Our Location'}
                </h3>
                <p className="text-gray-600">{content.info.address}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;